/**
 * Culture stage — the mascot and the four columns it reads.
 *
 * The robot walks in from the left and reaches for the first of four stacks as
 * they rise: Entertainment, Lifestyle, Fashion — the firm's three categories —
 * and Culture, the read that sits above all three. It loops while on screen.
 * Each stack carries its mark — a clapperboard, a trophy, a shirt — and
 * Culture carries all three.
 *
 * The robot is a sprite sheet cut out of the mascot clip (its beige room and
 * its own blocks removed), stepped by `background-position`. The stacks and
 * their labels are plain elements, so the wording is real text: sharp at any
 * size, in the palette's colours, and editable here rather than in a video.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Clapperboard, Shirt, Trophy, type LucideIcon } from 'lucide-react';
import sprite from '../../../../assets/elf/ci-robot-sprite.webp';

const COLS = 8;
const ROWS = 6;
const FRAMES = 47;
const FPS = 12;
/** One pass of the walk and reach, then a rest on the last frame before it loops. */
const PLAY_MS = (FRAMES / FPS) * 1000;
const LOOP_MS = PLAY_MS + 3600;

/** Height of each stack as a share of the stage; the first meets the robot's hand. */
const STACKS: { name: string; height: number; icons: LucideIcon[]; lead?: boolean }[] = [
  { name: 'Entertainment', height: 20, icons: [Clapperboard] },
  { name: 'Lifestyle', height: 34, icons: [Trophy] },
  { name: 'Fashion', height: 50, icons: [Shirt] },
  // Culture is the three combined, so it wears all three marks.
  { name: 'Culture', height: 64, icons: [Clapperboard, Trophy, Shirt], lead: true },
];

export const ElfCultureStage: React.FC = () => {
  const stageRef = useRef<HTMLDivElement>(null);
  const roboRef = useRef<HTMLDivElement>(null);
  /** Bumped each loop: it re-keys the stacks so their CSS entrance replays. */
  const [cycle, setCycle] = useState(0);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.25 });
    io.observe(stage);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const robo = roboRef.current;
    if (!robo || !inView) return;
    let raf = 0;
    let began = performance.now();
    let shown = -1;
    const tick = (now: number) => {
      let t = now - began;
      if (t >= LOOP_MS) {
        began = now;
        t = 0;
        setCycle((c) => c + 1);
      }
      const f = Math.min(FRAMES - 1, Math.floor((t / 1000) * FPS));
      if (f !== shown) {
        shown = f;
        robo.style.backgroundPosition = `${((f % COLS) / (COLS - 1)) * 100}% ${
          (Math.floor(f / COLS) / (ROWS - 1)) * 100
        }%`;
      }
      raf = requestAnimationFrame(tick);
    };
    setCycle((c) => c + 1);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  return (
    <div
      ref={stageRef}
      className="cistage"
      role="img"
      aria-label="The ELF-CIRCLE mascot reading four rising columns: Entertainment, Lifestyle, Fashion and Culture"
    >
      <span className="cistage__floor" aria-hidden="true" />
      <div
        ref={roboRef}
        className="cistage__robo"
        aria-hidden="true"
        style={{ backgroundImage: `url(${sprite})`, backgroundSize: `${COLS * 100}% ${ROWS * 100}%` }}
      />
      <div className="cistage__stacks" key={cycle} aria-hidden="true">
        {STACKS.map((s, i) => (
          <div
            key={s.name}
            className={s.lead ? 'cistack cistack--lead' : 'cistack'}
            style={{ height: `${s.height}%`, animationDelay: `${0.9 + i * 0.28}s` }}
          >
            <span className="cistack__tag" style={{ animationDelay: `${1.7 + i * 0.28}s` }}>
              {s.name}
            </span>
            <span className="cistack__marks" style={{ animationDelay: `${1.75 + i * 0.28}s` }}>
              {s.icons.map((Icon, n) => (
                <Icon key={n} className="cistack__mark" strokeWidth={2.2} />
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ElfCultureStage;
